import { parseISO } from "date-fns"
import type { StatusKind } from "@/routes/_layout/quotes/-components/status-glyphs"
import { dateOf, dayOf, formatDay, isWeekend } from "./days"
import { type PlannedStep, type PlannedTask, WO20_STEPS } from "./wo-20"

export type SourceKind = "forecast" | "quote" | "work-order"

export type PlanStatus = "in-progress" | "scheduled" | "draft" | "completed"

export type StepState = "todo" | "doing" | "done"

export type Step = {
  id: string
  name: string
  days: number
  shop: string
  deps: string[]
  offset?: number
}

export type Span = { start: number; end: number }

export type Task = Span & { name: string }

export type Plan = {
  id: string
  tail: string
  model: string
  title: string
  source: string
  sourceKind: SourceKind
  status: PlanStatus
  owner: string
  steps: Step[]
  bars: Partial<Record<string, Span>>
  tasks: Partial<Record<string, Task[]>>
  overrides: Partial<Record<string, StepState>>
}

export const PLAN_STATUSES: readonly PlanStatus[] = [
  "in-progress",
  "scheduled",
  "draft",
  "completed",
]

export const STEP_STATES: readonly StepState[] = ["todo", "doing", "done"]

export function planStatusLabel(status: PlanStatus): string {
  switch (status) {
    case "in-progress":
      return "In progress"
    case "scheduled":
      return "Scheduled"
    case "draft":
      return "Draft"
    case "completed":
      return "Completed"
  }
}

export function planStatusKind(status: PlanStatus): StatusKind {
  switch (status) {
    case "in-progress":
      return "in_progress"
    case "scheduled":
      return "pending"
    case "draft":
      return "draft"
    case "completed":
      return "done"
  }
}

export function stepStateLabel(state: StepState): string {
  switch (state) {
    case "todo":
      return "Not started"
    case "doing":
      return "In progress"
    case "done":
      return "Done"
  }
}

export function stepStateKind(state: StepState): StatusKind {
  switch (state) {
    case "todo":
      return "draft"
    case "doing":
      return "in_progress"
    case "done":
      return "done"
  }
}

type Template = [
  name: string,
  days: number,
  shop: string,
  deps: number[],
  offset?: number,
]

const PC12_SEQUENCE: Template[] = [
  ["Preliminary", 1, "Pilatus", []],
  ["Pre-inspection / engine runs / interior removal", 2, "Pilatus", [0]],
  ["Pre-jack phase", 1, "Pilatus", [1]],
  ["Function checks", 1, "Avionics", [2]],
  ["Engine area", 3, "Pilatus", [2]],
  ["Wings", 3, "Structures", [2]],
  ["Empennage / rear fuselage", 2, "Structures", [2]],
  ["Gear area", 2, "Pilatus", [2]],
  ["Flight control cables", 2, "Structures", [5, 6]],
  ["Fuselage interior / exterior", 2, "Interiors", [3]],
  [
    "Post-maintenance engine runs / install interior",
    1,
    "Pilatus",
    [4, 7, 8, 9],
  ],
  ["Pitot and static leak checks", 1, "Avionics", [10]],
  ["Aircraft wash / final interior checks", 1, "Interiors", [11]],
  ["Final: turnbuckle safety check", 1, "Pilatus", [12]],
]

const GENERIC_SEQUENCE: Template[] = [
  ["Preliminary", 1, "Shop", []],
  ["Incoming checks / interior removal", 2, "Interiors", [0]],
  ["Open-up", 2, "Structures", [1]],
  ["Zonal inspection", 4, "Structures", [2]],
  ["Systems tests", 2, "Avionics", [2]],
  ["Rectification", 3, "Shop", [3, 4]],
  ["Close-up", 2, "Structures", [5]],
  ["Engine runs", 1, "Shop", [6]],
  ["Final", 1, "Shop", [7]],
]

const TASK_NAMES: Partial<Record<string, string[]>> = {
  Preliminary: [
    "Sign off Preliminary Inspection form QA-004",
    "Install out-of-service sign & disabled-systems chart",
    "Document and store customer items",
    "Video exterior and interior, flaps 40°",
  ],
  "Pre-jack phase": [
    "Remove panels, check drain holes",
    "Lift on jacks and ground aircraft",
    "Remove battery for servicing",
  ],
  "Function checks": [
    "Pressurisation dump / manual control test",
    "Heater thermal protection switch tests",
    "Stick pusher check",
  ],
  "Engine area": [
    "Open cowlings, examine for cracks and chafing",
    "Borescope first-stage compressor blades",
    "Oil filter: remove, examine, replace",
  ],
  Wings: [
    "Examine wing attachment fittings and bolts",
    "Fuel tank vent and drain valves",
    "Flap tracks and rollers",
  ],
  "Gear area": [
    "Main gear trunnion and drag brace",
    "Brake wear indicators and discs",
    "Retraction test",
  ],
}

const DEFAULT_TASK_NAMES = [
  "Open access",
  "Inspect per AMM",
  "Rectify findings",
  "Close up",
]

const TASK_REFS: Partial<Record<string, string[]>> = {
  Preliminary: ["00-00", "00-00", "00-00", "00-00"],
  "Pre-jack phase": ["06-00/218", "07-10/161", "24-30/520"],
  "Function checks": ["21-30/463", "21-40/330", "22-20/519"],
  "Engine area": ["71-00/120", "72-00/231", "79-20/142"],
  Wings: ["57-10/214", "28-10/126", "27-50/140"],
  "Gear area": ["32-10/310", "32-40/333", "32-30/350"],
}

const DEFAULT_TASK_REFS = ["06-00/218", "05-20/100", "05-50/200", "06-00/219"]

export type Stock = "Available" | "Low" | "Reserved" | "Out of stock"

export type Material = {
  number: string
  name: string
  stock: Stock
  state: string
}

function material(
  number: string,
  name: string,
  stock: Stock,
  state: string,
): Material {
  return { number, name, stock, state }
}

const PARTS: Partial<Record<string, Material[]>> = {
  "79-20/142": [
    material("3034649-01", "Oil filter element", "Available", "Reserved"),
    material("MS29513-220", "O-ring, filter housing", "Available", "Reserved"),
  ],
  "72-00/231": [material("PWC32017", "Borescope plug seal", "Low", "Reserved")],
  "32-40/333": [
    material(
      "164-05806",
      "Brake disc",
      "Out of stock",
      "On order · ETA Oct 14",
    ),
    material("066-10500", "Brake lining kit", "Available", "Reserved"),
  ],
  "24-30/520": [
    material(
      "RG-380E/44",
      "Battery, lead-acid 24 V",
      "Reserved",
      "Loaner fitted",
    ),
  ],
  "28-10/126": [
    material("CAV-170", "Fuel drain valve", "Available", "Reserved"),
  ],
}

const TOOLS: Partial<Record<string, Material[]>> = {
  "72-00/231": [
    material("PWC-BS-01", "Borescope kit", "Available", "Tool crib"),
    material("PWC-WA-12", "Compressor wash adapter", "Available", "Tool crib"),
  ],
  "79-20/142": [
    material(
      "TW-050",
      "Torque wrench 10–50 lb·in",
      "Available",
      "Calibrated to Feb 27",
    ),
  ],
  "07-10/161": [
    material("JK-3T", "Aircraft jacks, 3 t (×3)", "Reserved", "Hangar 2"),
    material("TS-PC12", "Tail stand", "Available", "Hangar 2"),
  ],
  "32-40/333": [
    material("BG-PC12", "Brake wear gauge", "Low", "Tool crib · 1 left"),
  ],
  "06-00/218": [
    material("PF-100", "Panel fastener kit", "Available", "Tool crib"),
  ],
}

const CREW: Partial<Record<string, string[]>> = {
  Pilatus: ["Erik Lund", "Owen Pratt", "Caleb Shaw", "Nina Varga"],
  Avionics: ["Matt Beale", "Nick Bonfiglio", "Thane Freeman", "Shaun Rich"],
  Structures: ["Larry Bower", "Shawn Allen", "Chris Johnson", "Mark Michaud"],
  Interiors: ["Leo Angulo", "Tony Dao", "Kevin Cope", "Tom Kraemer"],
  Powerplant: [
    "Magnus Ellery",
    "Cyrus Bellamy",
    "Hana Stroud",
    "Idris Fenwick",
  ],
}

const PLANNED_TASKS = new Map<string, PlannedTask[]>(
  WO20_STEPS.map((step) => [step.name, step.tasks]),
)

const PLANNED_BY_REF = new Map<string, PlannedTask>(
  WO20_STEPS.flatMap((step) => step.tasks.map((task) => [task.ref, task])),
)

const SHOP_CREW = ["Ray Castillo", "Troy Hensley", "Mia Okafor"]

function sequenceFor(model: string): Template[] {
  return model === "PC-12 NGX" ? PC12_SEQUENCE : GENERIC_SEQUENCE
}

function plannedSequence(steps: PlannedStep[]): Template[] {
  const positions = new Map(
    steps.map((step, position) => [step.name, position]),
  )
  return steps.map(({ name, days, shop, after, offset }) => [
    name,
    days,
    shop,
    after.map((dep) => {
      const position = positions.get(dep)
      if (position === undefined) throw new Error(`Unknown step: ${dep}`)
      return position
    }),
    offset,
  ])
}

function stepsFrom(sequence: Template[]): Step[] {
  return sequence.map(([name, days, shop, deps, offset], index) => ({
    id: `s${index}`,
    name,
    days,
    shop,
    deps: deps.map((dep) => `s${dep}`),
    offset,
  }))
}

function nextWorkingDay(day: number): number {
  let next = day
  while (isWeekend(next)) next++
  return next
}

function endAfter(start: number, days: number): number {
  let end = start
  let left = days - 1
  while (left > 0) {
    end++
    if (!isWeekend(end)) left--
  }
  return end
}

function shiftWorkingDays(day: number, count: number): number {
  return endAfter(nextWorkingDay(day), count + 1)
}

export function schedule(
  steps: Pick<Step, "id" | "days" | "deps" | "offset">[],
  start: number,
): Plan["bars"] {
  const bars: Plan["bars"] = {}
  let left = steps
  while (left.length) {
    const ready = left.filter((step) => step.deps.every((dep) => bars[dep]))
    if (!ready.length) break
    for (const step of ready) {
      const after = Math.max(
        shiftWorkingDays(start, step.offset ?? 0),
        ...step.deps.map((dep) => (bars[dep]?.end ?? start - 1) + 1),
      )
      const first = nextWorkingDay(after)
      bars[step.id] = { start: first, end: endAfter(first, step.days) }
    }
    left = left.filter((step) => !bars[step.id])
  }
  return bars
}

export function cascade(steps: Step[], bars: Plan["bars"]): Plan["bars"] {
  const next = { ...bars }
  const done = new Set<string>()
  let left = steps
  while (left.length) {
    const ready = left.filter((step) => step.deps.every((dep) => done.has(dep)))
    if (!ready.length) break
    for (const step of ready) {
      done.add(step.id)
      const bar = next[step.id]
      if (!bar || !step.deps.length) continue
      const earliest = Math.max(
        ...step.deps.map((dep) => (next[dep]?.end ?? bar.start - 1) + 1),
      )
      if (bar.start < earliest) {
        next[step.id] = { start: earliest, end: earliest + bar.end - bar.start }
      }
    }
    left = left.filter((step) => !done.has(step.id))
  }
  return next
}

export function barOf(plan: Plan, stepId: string): Span {
  return plan.bars[stepId] ?? { start: 0, end: 0 }
}

export function planSpan(plan: Plan): Span {
  const bars = plan.steps.map((step) => barOf(plan, step.id))
  return {
    start: Math.min(...bars.map((bar) => bar.start)),
    end: Math.max(...bars.map((bar) => bar.end)),
  }
}

export function stepRef(plan: Plan, stepId: string): string {
  const index = plan.steps.findIndex((step) => step.id === stepId)
  return `${plan.id}-${String(index + 1).padStart(2, "0")}`
}

export function isConflicted(plan: Plan, step: Step): boolean {
  const bar = barOf(plan, step.id)
  return step.deps.some((dep) => barOf(plan, dep).end >= bar.start)
}

export function conflictCount(plan: Plan): number {
  return plan.steps.filter((step) => isConflicted(plan, step)).length
}

function derivedState(plan: Plan, span: Span): StepState {
  if (plan.status === "completed") return "done"
  if (span.end < 0 && plan.status !== "draft") return "done"
  if (span.start <= 0 && span.end >= 0 && plan.status === "in-progress") {
    return "doing"
  }
  return "todo"
}

export function stepState(plan: Plan, stepId: string): StepState {
  return plan.overrides[stepId] ?? derivedState(plan, barOf(plan, stepId))
}

export function taskState(plan: Plan, step: Step, task: Task): StepState {
  if (derivedState(plan, barOf(plan, step.id)) === "done") return "done"
  return derivedState(plan, task)
}

export function stepProgress(plan: Plan, step: Step): number {
  const state = stepState(plan, step.id)
  if (state === "done") return 1
  if (state !== "doing") return 0
  const bar = barOf(plan, step.id)
  let elapsed = 0
  for (let day = bar.start; day < 0; day++) if (!isWeekend(day)) elapsed++
  return Math.min(1, elapsed / step.days)
}

export function showsProgress(plan: Plan): boolean {
  return plan.status === "in-progress" || plan.status === "completed"
}

function plannedTaskDeps(planned: PlannedTask[], index: number): number[] {
  return (planned[index]?.after ?? []).map((ref) => {
    const position = planned.findIndex((task) => task.ref === ref)
    if (position === -1) throw new Error(`Unknown task: ${ref}`)
    return position
  })
}

export function taskDeps(step: Step, index: number): number[] {
  const planned = PLANNED_TASKS.get(step.name)
  return planned ? plannedTaskDeps(planned, index) : []
}

function sequencedTasks(planned: PlannedTask[], start: number): Task[] {
  const bars = schedule(
    planned.map((_, index) => ({
      id: String(index),
      days: 1,
      deps: plannedTaskDeps(planned, index).map(String),
    })),
    start,
  )
  return planned.map((task, index) => ({
    name: task.name,
    ...(bars[String(index)] ?? { start, end: start }),
  }))
}

export function tasksOf(plan: Plan, step: Step): Task[] {
  const saved = plan.tasks[step.id]
  if (saved) return saved
  const bar = barOf(plan, step.id)
  const planned = PLANNED_TASKS.get(step.name)
  if (planned?.some((task) => task.after)) {
    return sequencedTasks(planned, bar.start)
  }
  const names =
    PLANNED_TASKS.get(step.name)?.map((task) => task.name) ??
    TASK_NAMES[step.name] ??
    DEFAULT_TASK_NAMES
  const span = bar.end - bar.start + 1
  const length = Math.max(1, Math.ceil(span / names.length))
  return names.map((name, index) => {
    const start = bar.start + Math.floor((index * span) / names.length)
    return { name, start, end: Math.min(bar.end, start + length - 1) }
  })
}

export function taskRef(step: Step, index: number): string {
  const refs =
    PLANNED_TASKS.get(step.name)?.map((task) => task.ref) ??
    TASK_REFS[step.name] ??
    DEFAULT_TASK_REFS
  return refs[index % refs.length] ?? ""
}

export function partsFor(ref: string): Material[] {
  return PARTS[ref] ?? PLANNED_BY_REF.get(ref)?.parts ?? []
}

export function toolsFor(ref: string): Material[] {
  return TOOLS[ref] ?? PLANNED_BY_REF.get(ref)?.tools ?? []
}

export function crewFor(step: Step): string[] {
  const crew = CREW[step.shop] ?? SHOP_CREW
  return crew.slice(
    0,
    Math.min(crew.length, Math.max(1, Math.ceil(step.days / 1.5))),
  )
}

export function phaseOf(index: number, count: number): string {
  const quarter = Math.ceil(count * 0.25)
  if (index < quarter) return "Preparation"
  if (index >= count - quarter) return "Close-out"
  return "Inspection"
}

export function weekOf(day: number): string {
  let monday = day
  while (dateOf(monday).getDay() !== 1) monday--
  return `Week of ${formatDay(monday)}`
}

export function initials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
}

type Seed = [
  id: string,
  tail: string,
  model: string,
  title: string,
  source: string,
  sourceKind: SourceKind,
  status: PlanStatus,
  owner: string,
  start: number,
]

const SEEDS: Seed[] = [
  [
    "PLN-001",
    "N278GS",
    "PC-24",
    "300 FH / 12 mo inspection",
    "WO-1199",
    "work-order",
    "in-progress",
    "Erik Lund",
    -1,
  ],
  [
    "PLN-002",
    "N521DC",
    "PC-12 NGX",
    "300 FH / 12 mo inspection",
    "Forecast",
    "forecast",
    "scheduled",
    "Larry Bower",
    6,
  ],
  [
    "PLN-003",
    "N654NB",
    "King Air 350",
    "Phase 3 + AD 2026-14-07",
    "Q-2290",
    "quote",
    "scheduled",
    "Ray Castillo",
    12,
  ],
  [
    "PLN-004",
    "N410GS",
    "Phenom 300",
    "1200 FH inspection",
    "Q-2311",
    "quote",
    "draft",
    "Matt Beale",
    11,
  ],
  [
    "PLN-005",
    "N877FX",
    "Falcon 2000",
    "B check",
    "Forecast",
    "forecast",
    "completed",
    "Pierre Aubert",
    -26,
  ],
]

export function buildPlan(
  plan: Omit<Plan, "steps" | "bars" | "tasks" | "overrides">,
  start: number,
  sequence: Template[] = sequenceFor(plan.model),
): Plan {
  const steps = stepsFrom(sequence)
  return {
    ...plan,
    steps,
    bars: schedule(steps, start),
    tasks: {},
    overrides: {},
  }
}

export function seedPlans(): Plan[] {
  const seeded = SEEDS.map(
    ([id, tail, model, title, source, sourceKind, status, owner, start]) =>
      buildPlan(
        { id, tail, model, title, source, sourceKind, status, owner },
        start,
      ),
  )
  const wo20 = buildPlan(
    {
      id: "PLN-006",
      tail: "N30621",
      model: "Cessna 560XL",
      title: "Annual / 24 mo / 800 FH inspection",
      source: "WO-20",
      sourceKind: "work-order",
      status: "scheduled",
      owner: "Finn Quinlan",
    },
    dayOf(parseISO("2026-11-05")),
    plannedSequence(WO20_STEPS),
  )
  return [...seeded, wo20]
}

export type Candidate = {
  id: string
  tail: string
  model: string
  title: string
  meta: string
  source: string
}

export const FORECAST_CANDIDATES: readonly Candidate[] = [
  {
    id: "f1",
    tail: "N521DC",
    model: "PC-12 NGX",
    title: "300 FH / 12 mo inspection",
    meta: "Due Nov 14 · 38 FH left",
    source: "Forecast",
  },
  {
    id: "f2",
    tail: "N789PC",
    model: "PC-24",
    title: "600 FH inspection",
    meta: "Due Dec 2 · 71 FH left",
    source: "Forecast",
  },
  {
    id: "f3",
    tail: "N40N",
    model: "PC-12 NGX",
    title: "12 mo inspection",
    meta: "Due Nov 20",
    source: "Forecast",
  },
  {
    id: "f4",
    tail: "N418KP",
    model: "King Air 350",
    title: "Phase 2",
    meta: "Due Dec 9 · 54 FH left",
    source: "Forecast",
  },
]

export const QUOTE_CANDIDATES: readonly Candidate[] = [
  {
    id: "q1",
    tail: "N410GS",
    model: "Phenom 300",
    title: "1200 FH inspection",
    meta: "Q-2311 · $42,300",
    source: "Q-2311",
  },
  {
    id: "q2",
    tail: "N11CP",
    model: "Citation CJ3",
    title: "Doc 10 inspection",
    meta: "Q-2308 · $18,900",
    source: "Q-2308",
  },
  {
    id: "q3",
    tail: "N929T",
    model: "Falcon 2000",
    title: "Doc 8 · landing gear",
    meta: "Q-2316 · $96,400",
    source: "Q-2316",
  },
]
